import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chart } from 'react-chartjs-2';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// 下の枠がどの点を出しているのかを、グラフ側にも縦線で示す。
// index は options.plugins.focusGuide.index から受け取る。
// react-chartjs-2 はプラグインの配列を作成時にしか渡さないので、
// 描画のたびに変わる値をクロージャで持たせると古いままになる。options なら毎回更新される。
const focusGuidePlugin = {
  id: 'focusGuide',
  afterDatasetsDraw: function (chart, args, opts) {
    const index = opts && opts.index;
    if (index === null || index === undefined || index < 0) return;

    const scale = chart.scales.x;
    const area = chart.chartArea;
    if (!scale || !area) return;

    const x = scale.getPixelForValue(index);
    if (x < area.left || x > area.right) return;

    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, area.top);
    ctx.lineTo(x, area.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(33, 37, 41, 0.45)';
    ctx.stroke();
    ctx.restore();
  }
};

// 競合調査のグラフ。順位（折れ線）と口コミ数（棒）を、URL の metric で切り替えて出す。
//
// 2026-08-27 に作り直した。それ以前は左右2軸に線と棒を重ねて30日ぶん出していたが、
// どちらも読めなかった。原因は2つあって、直し方も別だった。
//
//   順位：上位を争う店どうしは実力がほぼ同じで、毎日1位⇄2位で入れ替わる。日次のまま
//         線にすると、その入れ替わり（＝傾向ではないブレ）が6本ぶんギザギザに重なる。
//         → Rails 側で週にまとめてから渡し、色は自店だけに残す。
//   口コミ：競合の口コミは月1回しか取らない。日次で累計を並べても月初の値のコピーが
//         30本並ぶだけで、店ごとの差も出なかった。
//         → 月ごとの総件数（累計）を棒にし、期間は過去1年・カスタムだけに絞る。
//
// どちらを出すかは Rails 側（URL の metric）が決める。ここに状態を持たせると、
// 口コミのときに期間・まとめ方のボタンを出し分けられなくなる。
const CompetitorRankChart = ({
  metric,
  labels,
  labelMonths,
  datasets,
  reviewDatasets,
  rankAxisLabel,
  reviewAxisLabel
}) => {
  const reviews = reviewDatasets || [];
  const showRank = metric !== 'review' || reviews.length === 0;

  return showRank ? (
    <TrendChart kind="rank" labels={labels} labelMonths={labelMonths}
                datasets={datasets} axisLabel={rankAxisLabel} />
  ) : (
    <TrendChart kind="review" labels={labels} labelMonths={labelMonths}
                datasets={reviews} axisLabel={reviewAxisLabel} />
  );
};

// 順位（折れ線）と口コミ数（棒）で共通のグラフ。軸の向きと単位だけが違う。
//
// カーソルを合わせた位置に縦線を引き、その位置の全店の値を固定の枠に出すところは
// 両方で同じなので、1つにまとめてある。
// 線をクリックしたとみなす縦方向の許容幅（canvas の座標で何ピクセルまでか）。
// 順位の軸は1位ぶんがおよそ100pxなので、これくらい取らないと線に当てるのが難しい。
const LINE_HIT_PX = 45;

const TrendChart = ({ kind, labels, labelMonths, datasets, axisLabel }) => {
  const isRank = kind === 'rank';
  const months = labelMonths || [];

  // グラフの下の固定枠に出す点。グラフ上のカーソル位置で決まる。
  // ツールチップだと枠外にはみ出さないよう左右に反転してしまい、
  // どの日を指しているのか分かりにくいので、位置の動かない枠に出す。
  const [focusIndex, setFocusIndex] = useState(labels.length - 1);

  // カーソルがグラフから離れたらボックスも縦線も消す
  const [hovering, setHovering] = useState(false);

  // 色を消した店（place_id の配列）。**既定は空＝すべての店に色が付いている。**
  // クリックは「消す／戻す」の切り替えで、選んだ店を足していくのではなく
  // 見たくない店を間引いていく形（2026-09-09 に既定を反転した）。
  // 表は Rails が描いていて親子関係が無いので、window のカスタムイベントで受け取る
  // （送り側は show.html.erb の末尾）。
  const [dimmed, setDimmed] = useState([]);
  useEffect(function () {
    const handle = function (event) {
      setDimmed((event.detail && event.detail.keys) || []);
    };
    window.addEventListener('competitor:dimmed', handle);
    return function () { window.removeEventListener('competitor:dimmed', handle); };
  }, []);

  const chartRef = useRef(null);

  // カーソルが今どの点の上にあるかを求める。
  //
  // ここでピクセル値をそのまま使ってはいけない。レイアウトの <html> に zoom:80% が
  // かかっているため、ブラウザが返す offsetX（拡大前のレイアウト座標）と
  // getBoundingClientRect（拡大後の画面座標）で尺が食い違い、Chart.js 内蔵の
  // hover / tooltip はズレた位置を指してしまう。以前ズレていたのはこれが原因。
  //
  // 枠の左端からの距離を「枠の幅に対する割合」に直せば、どちらの尺で測っても同じ値に
  // なるので zoom の影響が消える。あとはその割合を canvas の座標へ戻して軸に聞く。
  const indexAtClientX = function (clientX) {
    const chart = chartRef.current;
    if (!chart || !chart.scales || !chart.scales.x) return null;

    const rect = chart.canvas.getBoundingClientRect();
    if (!rect.width) return null;

    const ratio = (clientX - rect.left) / rect.width;
    const value = chart.scales.x.getValueForPixel(ratio * chart.width);
    if (value === null || value === undefined || isNaN(value)) return null;

    // 軸の外（左右の余白）まで出たときは端の点に寄せる
    return Math.min(labels.length - 1, Math.max(0, Math.round(value)));
  };

  // クリックされた線・棒がどの店のものかを探して、表の行に伝える。
  //
  // Chart.js 内蔵の当たり判定（getElementsAtEventForMode）は使えない。<html> に zoom:80%
  // がかかっていて、ブラウザが渡すイベント座標と canvas の座標で尺が食い違うため
  // （indexAtClientX のコメントに同じ話がある）。枠に対する割合へ直してから canvas の
  // 座標に戻し、要素の位置と自分で突き合わせる。
  const shopKeyAtPoint = function (clientX, clientY) {
    const chart = chartRef.current;
    if (!chart || !chart.chartArea || !chart.width || !chart.height) return null;

    const rect = chart.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const x = ((clientX - rect.left) / rect.width) * chart.width;
    const y = ((clientY - rect.top) / rect.height) * chart.height;

    const keyOf = function (i) { return chart.data.datasets[i] && chart.data.datasets[i].shopKey; };

    if (!isRank) {
      // 棒は枠の中に入っているかで決める。1つの月に6本が横に並ぶので、
      // 近さで測ると隣の店を拾ってしまう。
      for (let i = 0; i < chart.data.datasets.length; i++) {
        if (!chart.isDatasetVisible(i)) continue;

        const bars = chart.getDatasetMeta(i).data;
        for (let j = 0; j < bars.length; j++) {
          const el = bars[j];
          if (!el || isNaN(el.x) || isNaN(el.y)) continue;

          const half = (el.width || 0) / 2;
          const top = Math.min(el.y, el.base);
          const bottom = Math.max(el.y, el.base);
          if (Math.abs(x - el.x) <= half && y >= top && y <= bottom) return keyOf(i);
        }
      }
      return null;
    }

    // 線は「押したところの縦位置に一番近い線」を選ぶ。点そのものを当てるのは細くて難しい。
    const index = indexAtClientX(clientX);
    if (index === null) return null;

    let best = null;
    for (let i = 0; i < chart.data.datasets.length; i++) {
      if (!chart.isDatasetVisible(i)) continue;

      const el = chart.getDatasetMeta(i).data[index];
      if (!el || el.skip || isNaN(el.y)) continue;   // 取れていない期間は点が無い

      const distance = Math.abs(y - el.y);
      if (best === null || distance < best.distance) best = { key: keyOf(i), distance: distance };
    }

    // どの線からも離れているときは何も選ばない（余白を押して色が変わると戸惑うため）
    return best && best.distance <= LINE_HIT_PX ? best.key : null;
  };

  // グラフを押したときに起きることは2つある。どちらも押した1回で同時に起きる。
  //
  //   ・押した点の月を、下の分析メモに伝える（8/29 の点なら8月のメモが出る）。
  //     線に当たっていなくても伝えるのは、縦線とボックスがカーソルに付いてきて
  //     「今どの点を指しているか」が見えている以上、その点を押したとみなすのが
  //     自然なため。週の点は、ラベルに出ている日付と同じで最初の日の月になる。
  //   ・線・棒に当たっていれば、その店を表の行に伝えて色を消す／戻す。
  //
  // 色を消した店の一覧は表側（show.html.erb のスクリプト）だけが持っていて、
  // その結果が competitor:dimmed で戻ってくる。
  const handleClick = function (event) {
    const index = indexAtClientX(event.clientX);
    if (index !== null && months[index]) {
      window.dispatchEvent(new CustomEvent('competitor:month', {
        detail: { month: months[index] }
      }));
    }

    const key = shopKeyAtPoint(event.clientX, event.clientY);
    if (!key) return;

    window.dispatchEvent(new CustomEvent('competitor:toggle', { detail: { key: key } }));
  };

  const handleMouseMove = function (event) {
    const index = indexAtClientX(event.clientX);
    if (index === null) return;

    if (!hovering) setHovering(true);
    if (index !== focusIndex) setFocusIndex(index);
  };

  // 期間を変えると点の数が変わるので、範囲外に残っていたら最後の点に寄せる
  const safeIndex =
    focusIndex >= 0 && focusIndex < labels.length ? focusIndex : labels.length - 1;

  const values = [];
  datasets.forEach(function (d) {
    d.data.forEach(function (v) { if (v !== null && v !== undefined) values.push(v); });
  });
  const maxValue = values.length ? Math.max.apply(null, values) : 10;

  // 点が増えるほど1本あたりが細くなるので、詰まってきたら線と点を細くする。
  const dense = labels.length > 20;

  // カーソルを動かすたびに作り直すと、そのつど全データが読み直されて重くなる。
  // 中身は props が変わったときしか変わらないので、そのまま使い回す。
  const data = useMemo(function () {
    return {
      labels: labels,
      datasets: datasets.map(function (d) {
        // 既定はすべての店に色を付ける。クリックで消された店だけ灰色に落とす。
        // 自店（常に赤）は消せない。
        const lit = d.own || dimmed.indexOf(d.shopKey) < 0;
        const color = lit ? d.shopColor : '#ced4da';

        if (!isRank) {
          return Object.assign({}, d, {
            yAxisID: 'y',
            backgroundColor: color,
            borderColor: color
          });
        }

        return Object.assign({}, d, {
          yAxisID: 'y',
          borderColor: color,
          backgroundColor: color,
          borderWidth: dense ? (lit ? 2.5 : 1) : (d.own ? 3 : lit ? 2.5 : 1.5),
          pointRadius: dense ? (lit ? 3 : 0) : (d.own ? 5 : lit ? 3 : 2),
          // 色の付いた線を灰色より前に描く
          order: lit ? 1 : 2
        });
      })
    };
  }, [labels, datasets, dense, isRank, dimmed]);

  // 順位は「小さいほど良い」ので軸を反転して1位を一番上に出す。
  // 週・月にまとめた点は平均なので 3.4 のような小数になる。そのときは 0.5 刻みにしないと、
  // 目盛りの間で線が動いて「どこにいるのか」が読み取りにくい。
  let scaleY;
  if (isRank) {
    const fractional = values.some(function (v) { return v % 1 !== 0; });
    const rankMax = Math.ceil(maxValue);
    scaleY = {
      position: 'left',
      reverse: true,
      min: 1,
      max: rankMax,
      ticks: {
        stepSize: rankMax <= 10 ? (fractional ? 0.5 : 1) : Math.ceil(rankMax / 10),
        maxTicksLimit: 21,
        callback: function (value) { return value + '位'; }
      },
      title: { display: true, text: axisLabel || '順位' }
    };
  } else {
    // 口コミ数の目盛り。Chart.js 任せだと「0 の次が 350」のように粗くなるので自分で決める。
    // 1・2・5・10…と刻みを上げていき、目盛りが12本以内に収まったところで止める。
    let step = 1;
    while (maxValue / step > 12) {
      const digits = Math.pow(10, Math.floor(Math.log10(step)));
      const lead = Math.round(step / digits);
      step = (lead === 1 ? 2 : lead === 2 ? 5 : 10) * digits;
    }
    scaleY = {
      position: 'left',
      beginAtZero: true,   // 棒の高さを比べるので、途中から始めてはいけない
      // 一番高い棒が天井に張りつかないよう、1目盛りぶん上に余白を取る
      max: Math.ceil(maxValue / step) * step + step,
      ticks: { stepSize: step, maxTicksLimit: 25 },
      title: { display: true, text: axisLabel || '口コミ数' }
    };
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // カーソルに合わせて描き直すので、そのたびに動きが入ると縦線が遅れて付いてくる
    animation: false,
    interaction: { mode: 'index', intersect: false, axis: 'x' },
    // 棒は6店ぶんが1ヶ月の枠を分け合う。棒どうしはくっつけず、少しだけ隙間を空ける。
    datasets: {
      bar: { categoryPercentage: 0.82, barPercentage: 0.92 }
    },
    scales: {
      y: scaleY,
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          font: { size: dense ? 11 : 13 }
        },
        grid: { display: false }
      }
    },
    plugins: {
      focusGuide: { index: hovering ? safeIndex : null },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          generateLabels: function (chart) {
            const base = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            return base.map(function (item) {
              const dataset = chart.data.datasets[item.datasetIndex];
              item.text = dataset.shopName || item.text;
              // 順位の線は競合を灰色にしているので、凡例の印だけは店ごとの色にする。
              // どの店を消したのかが分からなくなるため。
              if (dataset.shopColor) {
                item.fillStyle = dataset.shopColor;
                item.strokeStyle = dataset.shopColor;
              }
              return item;
            });
          }
        }
      },
      // 中身は自分で組んだボックスに出す。Chart.js のふきだしは、枠からはみ出さないよう
      // 左右に反転する動きが読みにくかったので使わない。
      tooltip: { enabled: false }
    }
  };

  // ボックスの1行ぶん。1店＝1データセット。
  const focusRows = datasets.map(function (d) {
    return {
      name: d.shopName || d.label,
      own: d.own,
      color: d.shopColor || d.borderColor,
      value: d.data[safeIndex]
    };
  });
  // 順位は小さいほど上、口コミ数は多いほど上。取れていない値は最後に回す。
  focusRows.sort(function (a, b) {
    const missing = 'missing';
    const x = a.value === null || a.value === undefined ? missing : a.value;
    const y = b.value === null || b.value === undefined ? missing : b.value;
    if (x === missing) return 1;
    if (y === missing) return -1;
    return isRank ? x - y : y - x;
  });

  // ボックスをカーソル位置に置く。ここも縦線と同じで、ピクセルではなく「枠の幅に対する
  // 割合」で指定する（<html> の zoom:80% があるため。indexAtClientX のコメント参照）。
  // canvas と重ねている入れ物は canvas と同じ大きさなので、% がそのまま使える。
  const chart = chartRef.current;
  let boxLeft = null;
  let boxTop = 0;
  if (hovering && chart && chart.scales && chart.scales.x && chart.chartArea && chart.width) {
    boxLeft = (chart.scales.x.getPixelForValue(safeIndex) / chart.width) * 100;
    boxTop = (chart.chartArea.top / chart.height) * 100;
  }

  // 右寄りの点では、ボックスを線の左側に出さないと枠からはみ出す
  const flip = boxLeft !== null && boxLeft > 55;

  return (
    <div
      style={{ height: '100%', position: 'relative', cursor: 'pointer' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={function () { setHovering(false); }}
      onClick={handleClick}
    >
      <Chart
        ref={chartRef}
        type={isRank ? 'line' : 'bar'}
        data={data}
        options={options}
        plugins={[focusGuidePlugin]}
      />

      {boxLeft !== null && (
        <div
          style={{
            position: 'absolute',
            left: boxLeft + '%',
            top: boxTop + '%',
            // 線の左右どちらに出すかで、自分の幅ぶんずらす
            transform: flip ? 'translate(calc(-100% - 12px), 8px)' : 'translate(12px, 8px)',
            // カーソルの下に敷くと、ボックスの上に乗った時点で mousemove が
            // 拾えなくなって位置が固まる。当たり判定を持たせない。
            pointerEvents: 'none',
            minWidth: '14em',
            padding: '7px 10px 8px',
            background: 'rgba(255, 255, 255, 0.97)',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            fontSize: '0.82em',
            lineHeight: 1.55,
            whiteSpace: 'nowrap'
          }}
        >
          <div
            style={{
              marginBottom: '4px',
              paddingBottom: '3px',
              borderBottom: '1px solid #e9ecef',
              fontWeight: 'bold'
            }}
          >
            {labels[safeIndex]}
            <span className="text-muted" style={{ marginLeft: '0.5em', fontWeight: 'normal' }}>
              {axisLabel || (isRank ? '順位' : '口コミ数')}
            </span>
          </div>

          {focusRows.map(function (row) {
            const missing = row.value === null || row.value === undefined;
            // 取れていない値は空欄にしない（数字を取り違えるため）。
            // 順位は「上位20件に入っていない」ので圏外、口コミはまだ引いていないので未取得。
            const text = missing
              ? (isRank ? '圏外' : '未取得')
              : row.value + (isRank ? '位' : '件');

            return (
              <div
                key={row.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: row.own ? 'bold' : 'normal'
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    flex: 'none',
                    width: '9px',
                    height: '9px',
                    borderRadius: '50%',
                    background: row.color,
                    marginRight: '6px'
                  }}
                />
                {/* 実データでは長い店名も入るので、幅が伸びすぎないよう切り詰める */}
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '11em',
                    marginRight: '0.8em'
                  }}
                >
                  {row.name}
                </span>
                <span style={{ flex: 'none' }}>
                  <strong>{text}</strong>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompetitorRankChart;
